import { useCallback, useEffect, useState } from 'react'
import { produce } from 'immer'
import {
  BulkUpdateStatusFormModel,
  ErrorSummary,
  ValidationCallback,
} from '../typings'
import { formatErrors, normalize } from '../validation'
import { useBulkActionContext } from '../../../BulkActionContext'
import { BulkUpdateStatusDTO } from '../../../typings'
import { useFormikContext } from 'formik'

export const useValidateRows = (validateRows: ValidationCallback) => {
  const {
    values: { salesFunnelId, salesFunnelStageId },
  } = useFormikContext<BulkUpdateStatusFormModel>()

  const {
    selectedRows,
    dispatch,
    state: { isValidating },
  } = useBulkActionContext()

  const [errorSummary, setErrorSummary] = useState<ErrorSummary>({
    count: 0,
    hasAlreadyClosed: false,
    hasUnmetReqs: false,
  })

  const updateState = useCallback(
    (dto?: BulkUpdateStatusDTO) => {
      const { errors, hasAlreadyClosed, hasUnmetReqs } = formatErrors(dto)
      dispatch({ type: 'setErrors', payload: errors })
      setErrorSummary((prev) =>
        produce(prev, (draft) => {
          draft.count = errors.size
          draft.hasAlreadyClosed = hasAlreadyClosed
          draft.hasUnmetReqs = hasUnmetReqs
        }),
      )
    },
    [dispatch],
  )

  useEffect(() => {
    let mounted = true

    const doValidation = async () => {
      dispatch({ type: 'startValidating' })
      const dto = await validateRows(
        normalize(selectedRows, {
          salesFunnelId,
          salesFunnelStageId,
          closedReason: '',
          customReason: '',
        }),
      )

      if (mounted) updateState(dto)
    }

    const shouldValidate = salesFunnelId !== '' && salesFunnelStageId !== ''
    if (shouldValidate) doValidation()
    else updateState()

    return () => {
      mounted = false
    }
  }, [
    dispatch,
    salesFunnelId,
    salesFunnelStageId,
    selectedRows,
    updateState,
    validateRows,
  ])

  return {
    errorSummary,
    loading: isValidating,
  }
}
